/**
 * Credentials, writable.
 *
 * The last read-only surface in the core write spine. `POST /api/credentials`
 * and `PUT /api/credentials/:id` have been audited and tested since 2026-08-20
 * and no screen called either, so the five passwords the imported fleet shares
 * between 369 assets could be attached to an asset and never renamed, rotated
 * or added to.
 *
 * ── 🔴 There is no reveal, and there is nowhere to put one ───────────────
 *
 * `005_grants.sql` gives the API role INSERT and UPDATE on the ciphertext
 * columns and deliberately NOT SELECT. The API can write a secret and can never
 * read one back — not through a bug, not through a careless route, not through
 * a debug endpoint added at 6pm. `cosmon_runner` is the only role that may read
 * them, because it is the only one that talks to a device.
 *
 * So this screen shows a credential's label, kind, username and **when it was
 * last rotated**, and never its secret. Somebody who has lost a device password
 * replaces it here; they do not recover it. That is a property of the schema,
 * not a decision this screen is making, and it is worth stating on the screen
 * so nobody goes hunting for a button that cannot exist.
 *
 * ── Leaving the secret alone ─────────────────────────────────────────────
 *
 * An empty secret box means "not rotating", and the body omits the key
 * entirely — see `credential-form.ts`. The server refuses an explicit empty
 * string on purpose, because that is what a form posting an untouched field
 * looks like, and neither should blank a working password.
 */

import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  Drawer,
  Empty,
  Field,
  Loading,
  Section,
  Toast,
  Toolbar,
  type ColumnDef,
} from './components/index.ts';
import {
  CREDENTIAL_KINDS,
  CredentialFormError,
  EMPTY_DRAFT,
  bodyFrom,
  draftOf,
  willRotate,
  type CredentialDraft,
  type CredentialRecord,
} from './credential-form.ts';

const ago = (iso: string | null): string => {
  if (iso === null) return 'never';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days < 1) return 'today';
  if (days === 1) return 'yesterday';
  return `${days}d ago`;
};

const failure = (err: unknown): string =>
  err instanceof CredentialFormError || err instanceof ApiError ? err.message : String(err);

export function CredentialsEditor(): React.JSX.Element {
  const [rows, setRows] = useState<CredentialRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<CredentialRecord | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await api.get<{ credentials: CredentialRecord[] }>('/api/credentials');
      setRows(next.credentials);
      setError(null);
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const columns = useMemo<ColumnDef<CredentialRecord, unknown>[]>(
    () => [
      { id: 'label', header: 'Label', accessorKey: 'label' },
      { id: 'kind', header: 'Kind', accessorKey: 'kind', size: 100 },
      {
        id: 'username',
        header: 'Username',
        accessorFn: (c) => c.username ?? '',
        cell: ({ getValue }) => <span className="mono">{(getValue() as string) || '—'}</span>,
      },
      {
        id: 'rotated',
        header: 'Rotated',
        accessorFn: (c) => c.rotated_at ?? '',
        cell: ({ row }) => <span className="muted">{ago(row.original.rotated_at)}</span>,
        size: 110,
      },
      {
        id: 'version',
        header: 'Version',
        accessorKey: 'key_version',
        cell: ({ getValue }) => <span className="mono muted">v{getValue() as number}</span>,
        size: 90,
        meta: { align: 'right' },
      },
    ],
    [],
  );

  if (error !== null && rows === null) return <Banner tone="fault">{error}</Banner>;
  if (rows === null) return <Loading rows={5} cols={5} label="Loading credentials" />;

  return (
    <>
      {error !== null && <Banner tone="fault">{error}</Banner>}
      {notice !== null && <Toast message={notice} tone="ok" onDismiss={() => setNotice(null)} />}

      <Toolbar
        end={
          <Button variant="primary" icon={<Plus size={14} />} onClick={() => setCreating(true)}>
            New credential
          </Button>
        }
      />

      <Section
        flush
        title="Credentials"
        summary="A secret can be replaced and never read back — the API can write the ciphertext and is deliberately not granted read."
      >
        <DataTable
          data={rows}
          columns={columns}
          getRowId={(c) => c.id}
          initialSort={[{ id: 'label', desc: false }]}
          onRowClick={setSelected}
          caption="Credentials"
          empty={
            <Empty
              title="No credentials yet"
              body="An asset dials with one of these. Add the first to attach it to a device."
            />
          }
        />
      </Section>

      {selected !== null && (
        <CredentialDrawer
          credential={selected}
          onClose={() => setSelected(null)}
          onSaved={(message) => {
            setSelected(null);
            setNotice(message);
            void load();
          }}
        />
      )}

      {creating && (
        <NewCredentialDrawer
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void load();
          }}
        />
      )}
    </>
  );
}

/** The fields the two forms share. `creating` is the only difference: a new credential needs a secret. */
function CredentialFields({
  draft,
  onChange,
  prefix,
  creating,
  disabled,
}: {
  draft: CredentialDraft;
  onChange: (patch: Partial<CredentialDraft>) => void;
  prefix: string;
  creating: boolean;
  disabled: boolean;
}): React.JSX.Element {
  const id = (name: string): string => `${prefix}-${name}`;
  const userpass = draft.kind === 'userpass';
  return (
    <>
      <Field
        id={id('label')}
        label="Label"
        hint="What an operator picks from a list on an asset. The fleet shares a handful of these between hundreds of assets, so the label is the only thing telling them apart."
      >
        <input
          id={id('label')}
          value={draft.label}
          onChange={(e) => onChange({ label: e.target.value })}
          disabled={disabled}
        />
      </Field>

      <Field id={id('kind')} label="Kind">
        <select
          id={id('kind')}
          value={draft.kind}
          onChange={(e) => onChange({ kind: e.target.value })}
          disabled={disabled}
        >
          {CREDENTIAL_KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </Field>

      {/*
        Said, not enforced. The CHECK constraint governs `kind` only, so a
        userpass credential with no username is accepted by the database and
        dials with `undefined` as the user — which reads in a device log as a
        WRONG password rather than a missing one. The route refuses it.
      */}
      <Field
        id={id('username')}
        label="Username"
        hint={userpass ? 'Required for a username/password credential.' : 'Not used by this kind.'}
      >
        <input
          id={id('username')}
          value={draft.username}
          onChange={(e) => onChange({ username: e.target.value })}
          disabled={disabled || !userpass}
        />
      </Field>

      {/*
        🔴 On edit, empty means "leave it alone", and the body omits the key
        rather than sending "". The server refuses an explicit empty string
        precisely because that is what an untouched form field looks like —
        see credential-form.ts.
      */}
      <Field
        id={id('secret')}
        label={creating ? 'Secret' : 'New secret'}
        hint={
          creating
            ? 'Sealed on the way in and never readable back. Keep a copy wherever your passwords live before saving — this screen cannot show it to you again.'
            : 'Leave empty to keep the current secret. Anything typed here replaces it for every asset using this credential, and the old value cannot be read back.'
        }
      >
        <input
          id={id('secret')}
          type="password"
          value={draft.secret}
          onChange={(e) => onChange({ secret: e.target.value })}
          disabled={disabled}
          autoComplete="new-password"
        />
      </Field>
    </>
  );
}

function CredentialDrawer({
  credential,
  onClose,
  onSaved,
}: {
  credential: CredentialRecord;
  onClose: () => void;
  onSaved: (message: string) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<CredentialDraft>(() => draftOf(credential));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rotating = willRotate(draft);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/credentials/${credential.id}`, bodyFrom(draft, { creating: false }));
      onSaved(
        rotating
          ? `Rotated ${draft.label}. Every asset using it now dials with the new secret.`
          : `Saved ${draft.label}. The secret was not changed.`,
      );
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={credential.label}
      subtitle={`${credential.kind} · v${credential.key_version} · rotated ${ago(credential.rotated_at)}`}
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}

        <CredentialFields
          draft={draft}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          prefix={`cred-${credential.id}`}
          creating={false}
          disabled={busy}
        />

        {rotating && (
          // Rotation is not reversible in any useful sense — the old value
          // cannot be read back to compare, so getting it wrong is discovered
          // by a fleet that stops authenticating.
          <Banner tone="warn" title="This will rotate the secret">
            Every asset using this credential dials with the new value from its next check. The
            old one cannot be recovered.
          </Banner>
        )}

        <div className="row">
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {rotating ? 'Save and rotate' : 'Save'}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
}

function NewCredentialDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState<CredentialDraft>(EMPTY_DRAFT);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/credentials', bodyFrom(draft, { creating: true }));
      onCreated();
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open onClose={onClose} title="New credential" subtitle="What an asset dials with">
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}

        <CredentialFields
          draft={draft}
          onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
          prefix="new-cred"
          creating
          disabled={busy}
        />

        <div className="row">
          <Button variant="primary" busy={busy} onClick={() => void create()}>
            Create
          </Button>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
